/**
 * ChatStarterPrompts — Clickable suggested prompts shown when chat is empty.
 *
 * Addresses retention insight: "Only 0% of users send their first message.
 * Add suggested starter prompts to lower the barrier."
 *
 * Shows contextual suggestions based on profile completion state.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { MasterProfile } from '../../types';

interface ChatStarterPromptsProps {
  onSelect: (text: string) => void;
  profile?: Partial<MasterProfile>;
}

interface StarterPrompt {
  emoji: string;
  labelKey: string;
  fallback: string;
  textKey: string;
  textFallback: string;
  /** Only show when this profile field is missing */
  when?: (p: Partial<MasterProfile>) => boolean;
}

const PROMPTS: StarterPrompt[] = [
  {
    emoji: '🔬',
    labelKey: 'starter.analyzeSkin',
    fallback: 'Analyze my skin',
    textKey: 'starter.textAnalyzeSkin',
    textFallback: 'Can you analyze my skin and recommend a routine?',
    when: (p) => !p.skin?.type,
  },
  {
    emoji: '🧴',
    labelKey: 'starter.routine',
    fallback: 'Build my routine',
    textKey: 'starter.textRoutine',
    textFallback: 'Help me build a skincare routine for my skin type',
    when: (p) => !!p.skin?.type,
  },
  {
    emoji: '💡',
    labelKey: 'starter.concerns',
    fallback: 'Help with my concerns',
    textKey: 'starter.textConcerns',
    textFallback: 'What products do you recommend for my skin concerns?',
  },
  {
    emoji: '🌿',
    labelKey: 'starter.ingredients',
    fallback: 'Ingredient check',
    textKey: 'starter.textIngredients',
    textFallback: 'What ingredients should I look for and avoid?',
  },
  {
    emoji: '🛍️',
    labelKey: 'starter.browse',
    fallback: 'Browse by concern',
    textKey: 'starter.textBrowse',
    textFallback: 'Show me top-rated products for my skin concerns',
    when: (p) => !!p.skin?.concerns?.length,
  },
  {
    emoji: '🎯',
    labelKey: 'starter.goals',
    fallback: 'Achieve my goals',
    textKey: 'starter.textGoals',
    textFallback: 'Help me set skincare goals and track my progress',
    when: (p) => !p.skin?.routineGoals?.length && !p.skin?.skinGoals?.length,
  },
  {
    emoji: '💰',
    labelKey: 'starter.budget',
    fallback: 'Best on a budget',
    textKey: 'starter.textBudget',
    textFallback: 'What are the best affordable skincare products?',
  },
];

export default function ChatStarterPrompts({ onSelect, profile = {} }: ChatStarterPromptsProps) {
  const { t } = useTranslation('chat');

  // Pick up to 4 relevant prompts based on profile state
  const relevant = PROMPTS.filter(p => !p.when || p.when(profile)).slice(0, 4);

  return (
    <div className="chat-starter-prompts">
      <p className="chat-starter-prompts__title">
        {t('starter.title', '✨ How can I help you today?')}
      </p>
      <div className="chat-starter-prompts__grid">
        {relevant.map((prompt, i) => (
          <button
            key={i}
            type="button"
            className="chat-starter-prompts__btn"
            onClick={() => onSelect(t(prompt.textKey, prompt.textFallback))}
          >
            <span className="chat-starter-prompts__emoji">{prompt.emoji}</span>
            <span className="chat-starter-prompts__label">
              {t(prompt.labelKey, prompt.fallback)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
